import { Component, effect, ElementRef, inject, input, Renderer2, untracked, viewChild } from '@angular/core';
import { Icon } from '../icon/icon';
import type { CssColor, FontWeight, IconName } from '@appTypes';
import { CSSValuePipe } from '@pipes/css-value-pipe';

@Component({
  selector: 'app-title',
  standalone: true,
  imports: [ Icon, CSSValuePipe ],
  templateUrl: './title.html',
  styles: `
    @use 'mixins/utils' as *;
    :host {
      @include inline-block;
    }
  `,
})
export class Title {
  // Dependencias
  private readonly renderer = inject(Renderer2);

  // View refs
  $container = viewChild.required<ElementRef<HTMLSpanElement>>('container');

  // Inputs
  $text = input.required<string>({ alias: 'text' });
  $hierarchy = input<number, '1' | '2' | '3' | '4' | '5' | '6'>(4, { alias: 'hierarchy', transform: v => Number(v) });
  $textFontSize = input<string>(undefined, { alias: 'textFontSize' });
  $textTop = input<string>(undefined, { alias: 'textTop' });
  $textRight = input<string>(undefined, { alias: 'textRight' });
  $textBottom = input<string>(undefined, { alias: 'textBottom' });
  $textLeft = input<string>(undefined, { alias: 'textLeft' });
  $textWeight = input<FontWeight>('bold', { alias: 'textWeight' });
  $textColor = input<CssColor>('--black-white', { alias: 'textColor' });
  $gap = input<string>('--gap-small', { alias: 'gap' });
  $icon = input<IconName | null>(null, { alias: 'icon' });
  $iconColor = input<'original' | CssColor>('--primary-secondary', { alias: 'iconColor' });
  $iconSize = input<string>('', { alias: 'iconSize' });
  $iconScale = input<number>(1, { alias: 'iconScale' });
  $iconRotation = input<number>(0, { alias: 'iconRotation' });
  $iconRotationDelay = input<number>(0, { alias: 'iconRotationDelay' });
  $iconViewBoxAdjustmentX = input<number>(0, { alias: 'iconViewBoxAdjustmentX' });
  $iconViewBoxAdjustmentY = input<number>(0, { alias: 'iconViewBoxAdjustmentY' });

  // Signals / atributos
  heading: HTMLHeadElement | null = null;
  textNode: Text | null = null;

  constructor() {
    // Crear el heading
    effect(() => {
      this.createHeading();
    });

    // Actualización del heading
    effect(() => {
      if (this.textNode === null) return;

      // Actualizar texto
      this.textNode.data = this.$text();

      // Actualizar tamaño del texto
      const textFontSize = this.$textFontSize;
      textFontSize !== undefined && this.renderer.setStyle(this.heading, 'fontSize', textFontSize());

      // Posicionamiento top
      const textTop = this.$textTop();
      textTop !== undefined && this.renderer.setStyle(this.heading, 'top', textTop);

      // Posicionamiento right
      const textRight = this.$textRight();
      textRight !== undefined && this.renderer.setStyle(this.heading, 'right', textRight);

      // Posicionamiento bottom
      const textBottom = this.$textBottom();
      textBottom !== undefined && this.renderer.setStyle(this.heading, 'bottom', textBottom);

      // Posicionamiento left
      const textLeft = this.$textLeft();
      textLeft !== undefined && this.renderer.setStyle(this.heading, 'left', textLeft);

      // font weight y color
      this.renderer.setStyle(this.heading, 'fontWeight', this.$textWeight());
      this.renderer.setStyle(this.heading, 'color', `var(${ this.$textColor() })`);
    });
  }

  // Crea el elemento heading y lo agrega al DOM
  private createHeading() {
    if (this.heading !== null)
      this.renderer.removeChild(this.$container().nativeElement, this.heading);
    this.heading = this.renderer.createElement(`h${ this.$hierarchy() }`) as HTMLHeadElement;
    this.renderer.setStyle(this.heading, 'position', 'relative');
    this.textNode = this.renderer.createText(untracked(this.$text)) as Text;
    this.renderer.appendChild(this.heading, this.textNode);
    this.renderer.appendChild(this.$container().nativeElement, this.heading);
  }
}
